<template>
  <div v-if="localizedLinks.length" :class="$style.linkWrapper">
    <HgCollapsible
      v-for="(linkModule, index) in linkModules"
      :key="linkModule.title"
      :doExpandTransition="false"
      :class="$style.seoLinks"
    >
      <template #header>
        <h3 v-if="index > 0">{{ linkModule.title }}</h3>
        <h3 v-else>{{ props.title }}</h3>
      </template>
      <ul :class="$style.list" :style="`--column-count: ${columnCount}`">
        <li v-for="link in linkModule.links" :key="link.name">
          <a :href="link.url">
            <span>
              {{ link.name }}
            </span>
          </a>
        </li>
      </ul>
    </HgCollapsible>
  </div>
</template>

<script setup lang="ts">
import { HgCollapsible } from '@smg-real-estate/domus';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { type Locale, type LocalizedUrls } from '@/stores/useGeoStore';

interface Link {
  name: string;
  url: string;
}

interface LinkModuleModel {
  title: string;
  links: Link[];
}

const { locale } = useI18n();
const props = defineProps<{
  title: string;
  links: Record<Locale, LocalizedUrls> | null;
}>();

const CHUNK_SIZE = 120;

const localizedLinks = computed((): Link[] => props.links?.[locale.value] || []);
const columnCount = computed(() => (localizedLinks.value.length > CHUNK_SIZE ? 2 : 1));
const linkModules = computed((): LinkModuleModel[] => {
  if (!localizedLinks.value?.length || localizedLinks.value.length <= CHUNK_SIZE) {
    return [{ title: props.title, links: localizedLinks.value }];
  }

  const chunkTitle = props.title;
  const numberOfChunks = Math.ceil(localizedLinks.value.length / CHUNK_SIZE);
  const itemPerChunk = Math.ceil(localizedLinks.value.length / numberOfChunks);
  const chunks: LinkModuleModel[] = [];
  for (let i = 0; i < numberOfChunks; i++) {
    const chunk = localizedLinks.value.slice(i * itemPerChunk, (i + 1) * itemPerChunk);
    chunks.push({ title: `${chunkTitle} | ${i}`, links: chunk });
  }

  return chunks;
});
</script>

<style module lang="scss">
@use '@/styles/variables' as *;

.linkWrapper {
  display: flex;
  flex-direction: column;
  gap: $space-150;
}

.seoLinks {
  [class*='Collapsible_body'] {
    border-bottom: solid 1px $color-grey-50;
  }

  .list {
    padding-block-end: $space-50;
    padding-inline-start: 0;
    margin: 0;
    list-style: none;
    column-count: var(--column-count, 1);

    li::before {
      margin-right: $space-25;
      text-indent: -5px;
      content: '\2014';
    }
  }
}
</style>
